import { z } from 'zod';
/**
 * Report profiles: a named list of the parameters a device puts in its periodic reports, assigned to sites, devices
 * or many devices at once; Central writes the list to every device whose report differs
 * (docs/architecture/18-report-profiles.md).
 */
const Key = z.string().min(1).max(64);
export const ReportParameter = z.object({
    key: Key,
    label: z.string(),
    unit: z.string().optional(),
    group: z.string().optional(),
});
/** One protocol that supports report profiles: its parameters, factory list and list size limit (catalog data). */
export const ReportProtocol = z.object({
    protocol: z.string(),
    name: z.string(),
    max_keys: z.number().int().min(1),
    default_keys: z.array(Key),
    parameters: z.array(ReportParameter),
});
export const ReportCatalog = z.object({ protocols: z.array(ReportProtocol) });
export const ReportSyncStatus = z.enum(['not_supported', 'pending', 'in_progress', 'synced', 'failed']);
export const ReportProfile = z.object({
    id: z.uuid(),
    client_id: z.uuid().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    protocol: z.string(),
    listener: z.number().int(),
    keys: z.array(Key),
    sites: z.number().int(),
    devices: z.number().int(),
    /** managed devices per sync status */
    sync: z.record(z.string(), z.number().int()),
    can_manage: z.boolean(),
});
export const ReportProfileList = z.object({ items: z.array(ReportProfile) });
export const ReportProfileSave = z.object({
    client_id: z.uuid().nullable(),
    name: z.string().trim().min(1).max(120),
    description: z.string().max(2000).nullable().optional(),
    protocol: z.string().min(1).max(64),
    listener: z.number().int().min(0).max(127).default(0),
    keys: z.array(Key).min(1).max(64),
});
/** Targets to add to / remove from a profile: sites (with their sub-sites) and single devices. */
export const ReportProfileTargets = z
    .object({
    site_ids: z.array(z.uuid()).max(500).default([]),
    device_ids: z.array(z.uuid()).max(10000).default([]),
})
    .refine((t) => t.site_ids.length + t.device_ids.length > 0, { message: 'empty' });
export const ReportProfileAssignments = z.object({
    sites: z.array(z.object({ site_id: z.uuid(), name: z.string(), path_names: z.array(z.string()) })),
    devices: z.array(z.object({ device_id: z.uuid(), name: z.string() })),
});
/** A device of the profile's protocol in the client: its effective profile and where its sync stands. */
export const ReportDevice = z.object({
    device_id: z.uuid(),
    name: z.string(),
    site_name: z.string().nullable(),
    presence: z.string().nullable(),
    profile_id: z.uuid().nullable(),
    profile_name: z.string().nullable(),
    /** where the effective profile comes from */
    source: z.enum(['device', 'site']).nullable(),
    status: ReportSyncStatus.nullable(),
    attempts: z.number().int(),
    error: z.string().nullable(),
    synced_at: z.string().nullable(),
    verified_at: z.string().nullable(),
    /** the device's last reported parameter list equals its effective profile (null = no report seen yet) */
    report_matches: z.boolean().nullable(),
});
export const ReportDeviceList = z.object({ items: z.array(ReportDevice), total: z.number().int() });
//# sourceMappingURL=reportProfiles.js.map